import { api } from "@/features/utils/fetch-server";
import { JSONGeneratorAPI } from "@/features/utils/api/json-generator";
import type {
  CreateFormRequest,
  QualificationForm,
  QualificationStats,
  Qualification,
  SearchQualificationsResponse,
  SubmitQualificationRequest,
  UpdateFormRequest,
  UpdateQualificationRequest,
  QualificationFormTemplate,
  CreateFormFromTemplateRequest,
  AuditHistoryEntry,
} from "../types";

export class QualificationsAPI {
  // Forms
  static async listForms(
    clientId: string,
    options?: {
      status?: "active" | "inactive" | "draft";
    },
  ) {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);

    const queryString = params.toString();
    const url = `/api/qualifications/v1/${clientId}/forms${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await api.get<{ forms: QualificationForm[] }>(url);
    return response.forms;
  }

  static async getForm(clientId: string, formId: string) {
    const response = await api.get<{ form: QualificationForm }>(
      `/api/qualifications/v1/${clientId}/forms/${formId}`,
    );
    return response.form;
  }

  static async createForm(clientId: string, data: CreateFormRequest) {
    const response = await api.post<
      { form: QualificationForm },
      CreateFormRequest
    >(`/api/qualifications/v1/${clientId}/forms`, data);
    return response.form;
  }

  static async generateForm(
    prompt: string,
    options?: { url?: string },
  ): Promise<
    Omit<QualificationForm, "id" | "client_id" | "created_at" | "updated_at">
  > {
    return await JSONGeneratorAPI.generate({
      prompt,
      url: options?.url,
      json_id: "qualification_form",
    });
  }

  static async updateForm(
    clientId: string,
    formId: string,
    data: UpdateFormRequest,
  ) {
    const response = await api.put<
      { form: QualificationForm },
      UpdateFormRequest
    >(`/api/qualifications/v1/${clientId}/forms/${formId}`, data);
    return response.form;
  }

  static async deleteForm(clientId: string, formId: string): Promise<void> {
    await api.delete(`/api/qualifications/v1/${clientId}/forms/${formId}`);
  }

  // Qualifications
  static async searchQualifications(
    clientId: string,
    options?: {
      search?: string;
      limit?: number;
      offset?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
      customer_id?: string;
      form_id?: string;
      status?: string;
      date_from?: string;
      date_to?: string;
      score_min?: number;
      score_max?: number;
      completed?: boolean;
      reviewed?: boolean;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.score_min !== undefined)
      params.append("score_min", String(options.score_min));
    if (options?.score_max !== undefined)
      params.append("score_max", String(options.score_max));
    if (options?.completed !== undefined)
      params.append("completed", String(options.completed));
    if (options?.reviewed !== undefined)
      params.append("reviewed", String(options.reviewed));
    if (options?.search) params.append("search", options.search);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    if (options?.sort_by) params.append("sort_by", options.sort_by);
    if (options?.sort_order) params.append("sort_order", options.sort_order);
    if (options?.customer_id) params.append("customer_id", options.customer_id);
    if (options?.form_id) params.append("form_id", options.form_id);
    if (options?.status) params.append("status", options.status);
    if (options?.date_from) params.append("date_from", options.date_from);
    if (options?.date_to) params.append("date_to", options.date_to);

    const queryString = params.toString();
    const url = `/api/qualifications/v1/${clientId}/qualifications/search${
      queryString ? `?${queryString}` : ""
    }`;

    return await api.get<SearchQualificationsResponse>(url);
  }

  static async getQualification(clientId: string, qualificationId: string) {
    const response = await api.get<{ qualification: Qualification }>(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}`,
    );
    return response.qualification;
  }

  static async submitQualification(
    clientId: string,
    data: SubmitQualificationRequest,
  ) {
    const response = await api.post<
      { qualification: Qualification },
      SubmitQualificationRequest
    >(`/api/qualifications/v1/${clientId}/qualifications`, data);
    return response.qualification;
  }

  static async updateQualification(
    clientId: string,
    qualificationId: string,
    data: UpdateQualificationRequest,
  ) {
    const response = await api.put<
      { qualification: Qualification },
      UpdateQualificationRequest
    >(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}`,
      data,
    );
    return response.qualification;
  }

  static async deleteQualification(
    clientId: string,
    qualificationId: string,
  ): Promise<void> {
    await api.delete(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}`,
    );
  }

  static async approveQualification(clientId: string, qualificationId: string) {
    const response = await api.post<
      { qualification: Qualification },
      undefined
    >(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}/approve`,
      undefined,
    );
    return response.qualification;
  }

  /** Undo a decision: back to pending. */
  static async reopenQualification(clientId: string, qualificationId: string) {
    const response = await api.post<
      { qualification: Qualification },
      undefined
    >(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}/reopen`,
      undefined,
    );
    return response.qualification;
  }

  static async rejectQualification(clientId: string, qualificationId: string) {
    const response = await api.post<
      { qualification: Qualification },
      undefined
    >(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}/reject`,
      undefined,
    );
    return response.qualification;
  }

  static async getQualificationHistory(
    clientId: string,
    qualificationId: string,
  ) {
    const response = await api.get<{ history: AuditHistoryEntry[] }>(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}/history`,
    );
    return response.history;
  }

  static async recalculateQualification(
    clientId: string,
    qualificationId: string,
  ) {
    const response = await api.post<
      { qualification: Qualification },
      undefined
    >(
      `/api/qualifications/v1/${clientId}/qualifications/${qualificationId}/recalculate`,
      undefined,
    );
    return response.qualification;
  }

  static async getStats(clientId: string) {
    return await api.get<QualificationStats>(
      `/api/qualifications/v1/${clientId}/qualifications/stats`,
    );
  }

  // Templates
  static async listTemplates(
    clientId: string,
    category?: QualificationFormTemplate["category"],
  ) {
    const params = new URLSearchParams();
    if (category) params.append("category", category);

    const queryString = params.toString();
    const url = `/api/qualifications/v1/${clientId}/templates${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await api.get<{ data: QualificationFormTemplate[] }>(url);
    return response.data;
  }

  static async getTemplate(clientId: string, templateKey: string) {
    const response = await api.get<{ data: QualificationFormTemplate }>(
      `/api/qualifications/v1/${clientId}/templates/${templateKey}`,
    );
    return response.data;
  }

  static async createFormFromTemplate(
    clientId: string,
    templateKey: string,
    data?: CreateFormFromTemplateRequest,
  ) {
    const response = await api.post<
      { data: QualificationForm },
      CreateFormFromTemplateRequest | undefined
    >(
      `/api/qualifications/v1/${clientId}/templates/${templateKey}/create-form`,
      data,
    );
    return response.data;
  }
}
